import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { mergeBlockIndentAttrs } from '../lib/post_block_indent.js';
import { PostImageCarouselView } from '../components/PostImageCarouselView.jsx';
import { captionInnerHtmlToRenderChildren } from '../lib/carousel_caption_html.js';
import { slideBlobDomProps } from '../lib/carousel_blob_refresh.js';

// Normalises raw slide attributes coming from Tiptap state, parsed HTML, or
// the resource modal's confirm helpers. `resourceId` is optional and only
// preserved when present (legacy carousels inserted before this change have
// no ids — that's fine, the editor will still match them by URL).
function safeSlides(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s) => s && typeof s.src === 'string' && s.src.length > 0)
    .map((s) => {
      const slide = {
        type: s.type === 'video' ? 'video' : 'image',
        src: s.src,
        poster: typeof s.poster === 'string' ? s.poster : '',
        caption: typeof s.caption === 'string' ? s.caption : '',
        alt: typeof s.alt === 'string' ? s.alt : '',
      };
      const rid = s.resourceId ?? s.resource_id ?? null;
      if (rid !== null && rid !== undefined && rid !== '') {
        slide.resourceId = String(rid);
      }
      const sid = s.signedId ?? s.signed_id ?? null;
      if (sid) slide.signedId = String(sid);
      const psid = s.posterSignedId ?? s.poster_signed_id ?? null;
      if (psid) slide.posterSignedId = String(psid);
      return slide;
    });
}

function staticSlideSpec(slide, i) {
  const blobAttrs = slideBlobDomProps(slide);
  const media =
    slide.type === 'video'
      ? [
          'video',
          {
            src: slide.src,
            poster: slide.poster || undefined,
            class: 'post-content-carousel__video',
            controls: 'controls',
            playsinline: 'playsinline',
            preload: 'metadata',
            ...blobAttrs,
          },
        ]
      : [
          'img',
          {
            src: slide.src,
            alt: slide.alt || '',
            class: 'post-content-carousel__img',
            ...blobAttrs,
          },
        ];
  const slideAttrs = {
    class: 'post-content-carousel__slide swiper-slide',
    'data-carousel-index': String(i),
  };
  if (slide.resourceId) {
    slideAttrs['data-resource-id'] = String(slide.resourceId);
  }
  return ['div', slideAttrs, media];
}

function counterSpec(current, total) {
  return [
    'span',
    { class: 'post-content-carousel__counter' },
    ['span', { class: 'post-content-carousel__counter-num', 'data-carousel-counter-current': '1' }, String(current)],
    ['span', { class: 'post-content-carousel__counter-of' }, 'of'],
    ['span', { class: 'post-content-carousel__counter-num', 'data-carousel-counter-total': '1' }, String(total)],
  ];
}

export const PostImageCarousel = Node.create({
  name: 'postImageCarousel',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      slides: {
        default: [],
        parseHTML: (element) => {
          const raw = element.getAttribute('data-ps-carousel-slides');
          if (!raw) return [];
          try {
            return safeSlides(JSON.parse(raw));
          } catch {
            return [];
          }
        },
        renderHTML: (attributes) => {
          const slides = safeSlides(attributes.slides);
          return {
            'data-ps-carousel-slides': JSON.stringify(slides),
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-post-carousel]',
        getAttrs: (el) => {
          const raw = el.getAttribute('data-ps-carousel-slides');
          if (!raw) return false;
          try {
            return { slides: safeSlides(JSON.parse(raw)) };
          } catch {
            return false;
          }
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const slides = safeSlides(node.attrs.slides);
    const slideNodes = slides.map((s, i) => staticSlideSpec(s, i));
    const captionNodes = slides.map((s, i) => {
      const inner = typeof s.caption === 'string' ? s.caption : '';
      const body = captionInnerHtmlToRenderChildren(inner);
      const pAttrs = {
        class: 'post-content-carousel__caption ps-post-style-caption',
        'data-carousel-caption': String(i),
        style: i === 0 ? '' : 'display: none;',
      };
      if (body === '' || body == null) return ['p', pAttrs, ''];
      if (!Array.isArray(body)) return ['p', pAttrs, body];
      // Single DOMOutputSpec: ['tag', attrs?, ...children]
      if (
        typeof body[0] === 'string' &&
        body.length >= 2 &&
        body[1] !== null &&
        typeof body[1] === 'object' &&
        !Array.isArray(body[1])
      ) {
        return ['p', pAttrs, body];
      }
      return ['p', pAttrs, ...body];
    });
    const nav = [
      'div',
      { class: 'post-content-carousel__nav' },
      [
        'button',
        {
          type: 'button',
          class: 'post-content-carousel__nav-btn',
          'data-carousel-prev': '1',
          'aria-label': 'Previous slide',
        },
        ['span', { class: 'icon-ps-arrow-left', 'aria-hidden': 'true' }],
      ],
      counterSpec(1, slides.length || 0),
      [
        'button',
        {
          type: 'button',
          class: 'post-content-carousel__nav-btn',
          'data-carousel-next': '1',
          'aria-label': 'Next slide',
        },
        ['span', { class: 'icon-ps-arrow-right', 'aria-hidden': 'true' }],
      ],
    ];
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-post-carousel': '1',
        'data-controller': 'post-carousel',
        class: 'post-content-carousel',
        'data-ps-carousel-slides': JSON.stringify(slides),
      }),
      [
        'div',
        { class: 'post-content-carousel__track swiper' },
        ['div', { class: 'post-content-carousel__track-inner swiper-wrapper' }, ...slideNodes],
      ],
      [
        'div',
        { class: 'post-content-carousel__caption-wrap' },
        ...captionNodes,
      ],
      ['div', { class: 'post-content-carousel__rule', 'aria-hidden': 'true' }],
      nav,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PostImageCarouselView);
  },
});

export function insertPostImageCarousel(slides, state) {
  const normalized = safeSlides(slides);
  if (normalized.length === 0) return false;
  return {
    type: 'postImageCarousel',
    attrs: mergeBlockIndentAttrs(state, { slides: normalized }),
  };
}
